"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withTimeout = void 0;
/**
 * Wait on a promise and include a timeout, in case the promise takes too long or will never
 * return. If the underlying promise throws, that error will be rethrown. If a DOMException -
 * TimeoutError is thrown, the promise is resolved with `{ ok: false }`.
 * @arg {Promise<T>} promise The promise to wait on while checking for a timeout.
 * @arg {number} [time=500] The time to wait, in milliseconds, before returning.
 * @returns {WithTimeoutResult<T>} If the promise completes, or the operation times out, this
 * promise is resolved with a `WithTimeoutResult` object. If the promise rejects, and the operation
 * has not timed out, the rejection is propagated.
 */
async function withTimeout(promise, time = 500) {
    let shouldReject = true;
    const timeout = new Promise((_resolve, reject) => {
        function handleReject() {
            if (shouldReject) {
                if (this instanceof AbortSignal) {
                    reject(this.reason);
                    return;
                }
                reject(new DOMException('The operation timed out.', 'TimeoutError'));
            }
        }
        if (!('timeout' in AbortSignal)) {
            setTimeout(handleReject, time);
            return;
        }
        const signal = AbortSignal.timeout(time);
        signal.addEventListener('abort', handleReject);
    });
    return new Promise(async (resolve, reject) => {
        try {
            const result = await Promise.race([promise, timeout]);
            shouldReject = false;
            resolve({ ok: true, result });
        }
        catch (e) {
            if (e instanceof DOMException && e.name === 'TimeoutError') {
                resolve({ ok: false });
                return;
            }
            reject(e);
        }
    });
}
exports.withTimeout = withTimeout;
//# sourceMappingURL=utils.js.map